import React, { memo, useMemo } from 'react';

import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import { getIsRecipientPreviewable } from '../../DisclosureDelivery.utils';
import useDisclosureDelivery from '../../hooks/useDisclosureDelivery';
import useSignatureRequestDocumentViewer from '../../hooks/useSignatureRequestDocumentViewer';

import {
  getRecipientCopyButtonConfig,
  getRecipientCopyFileName,
} from './DisclosureRecipientCopyButton.utils';

const DisclosureRecipientCopyButton = ({
  recipientRequestId,
  documentId = null,
  formDeliveryRequestId = null,
  isFenced = false,
  packageStatus = null,
  signerFullName = null,
  formBundleName = null,
  signedDate = null,
  status = null,
}) => {
  const { isReadAllowed, refresh } = useDisclosureDelivery();

  const isPreview = getIsRecipientPreviewable({ documentId, isFenced, packageStatus, status });

  const previewRequest = useMemo(
    () => (isPreview ? { formDeliveryRequestId, recipientRequestId } : null),
    [isPreview, formDeliveryRequestId, recipientRequestId],
  );

  const buttonConfig = useMemo(
    () => getRecipientCopyButtonConfig({ isPreview, recipientRequestId, signerFullName }),
    [isPreview, recipientRequestId, signerFullName],
  );

  const { onViewDocuments } = useSignatureRequestDocumentViewer({
    documentId,
    header: buttonConfig.header,
    fileName: getRecipientCopyFileName({ formBundleName, signerFullName }),
    fileDate: signedDate,
    previewRequest,
    notice: buttonConfig.notice,
    isDownloadable: buttonConfig.isDownloadable,
    isPrintable: buttonConfig.isPrintable,
    onConflict: refresh,
  });

  if (!isReadAllowed || !(documentId || isPreview)) {
    return null;
  }

  return (
    <ButtonWithTooltip
      aria-label={buttonConfig.ariaLabel}
      dataTestId={buttonConfig.dataTestId}
      icon={buttonConfig.icon}
      iconButtonClassName="p-4"
      iconSize={IconSize[16]}
      title={buttonConfig.title}
      onClick={onViewDocuments}
    />
  );
};

DisclosureRecipientCopyButton.propTypes = {
  recipientRequestId: PropTypes.string.isRequired,
  documentId: PropTypes.string,
  formDeliveryRequestId: PropTypes.string,
  isFenced: PropTypes.bool,
  packageStatus: PropTypes.string,
  signerFullName: PropTypes.string,
  formBundleName: PropTypes.string,
  signedDate: PropTypes.string,
  status: PropTypes.string,
};

export default memo(DisclosureRecipientCopyButton);
